import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityPicker, itemRender, ITEM_URL } from "@/components/app/EntityPicker";
import { useMe } from "@/components/app/MeContext";
import { api, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { LookupSelect } from "@/components/app/LookupSelect";

const EMPTY = { item: null, description: "", uom: "Nos", qty: "1", unit_price: "0", tax_pct: "18" };

function LineDialog({ open, onOpenChange, order, line, onSaved }) {
  const init = useMemo(() => (line ? {
    item: line.item_code ? { ...line.item_snapshot, item_code: line.item_code } : null, description: line.description, uom: line.uom,
    qty: String(line.qty), unit_price: String(line.unit_price), tax_pct: String(line.tax_pct),
  } : EMPTY), [line]);
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const [prevInit, setPrevInit] = useState(init);
  if (prevInit !== init) { setPrevInit(init); setF(init); }

  const pickPart = (p) => setF({ ...f, item: p, description: p ? p.item_name : f.description, uom: p ? p.uom : f.uom,
    tax_pct: p && p.gst_percent != null ? String(p.gst_percent) : f.tax_pct,
    unit_price: p && p.price != null && f.unit_price === "0" ? String(p.price) : f.unit_price });

  const save = async () => {
    setBusy(true);
    try {
      const body = { item_code: f.item?.item_code || null, description: f.description, uom: f.uom, qty: Number(f.qty), unit_price: Number(f.unit_price), tax_pct: Number(f.tax_pct) };
      if (line) await api.put(`/orders/${order.id}/lines/${line.id}`, body);
      else await api.post(`/orders/${order.id}/lines`, body);
      onSaved();
      onOpenChange(false);
      setF(EMPTY);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="line-item-dialog">
        <DialogHeader><DialogTitle>{line ? `Edit line ${line.line_no}` : "Add line item"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Item (Item Master)</Label>
            <EntityPicker url={ITEM_URL} value={f.item} onChange={pickPart} render={itemRender} placeholder="Search item code, name, sub-group or make" testid="line-part-picker" itemFilters />
            {f.item && !f.item.hsn_sac && <p className="text-[11px] text-amber-700 mt-1" data-testid="line-hsn-warning">This item has no HSN / SAC yet — needed before invoicing.</p>}</div>
          <div className="col-span-2"><Label>Description *</Label><Input value={f.description} onChange={set("description")} data-testid="line-description-input" /></div>
          <div><Label>Qty *</Label><Input type="number" value={f.qty} onChange={set("qty")} data-testid="line-qty-input" /></div>
          <div><Label>UOM</Label>{f.item ? <Input value={`${f.item.uom} (from item)`} disabled data-testid="line-uom-input" /> : <LookupSelect type="unit" allowEmpty={false} value={f.uom} onChange={(v) => setF({ ...f, uom: v })} testid="line-uom-input" />}</div>
          <div><Label>Unit price (₹)</Label><Input type="number" value={f.unit_price} onChange={set("unit_price")} data-testid="line-price-input" /></div>
          <div><Label>GST %</Label><Input type="number" value={f.tax_pct} onChange={set("tax_pct")} data-testid="line-tax-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="line-save-btn">{busy ? "Saving…" : "Save line"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LineItemsTab({ order, reload }) {
  const { can } = useMe();
  const [dlg, setDlg] = useState({ open: false, line: null });
  const editable = (order.status === "draft" || (order.historical && !order.qty_dispatched)) && can("write");
  const remove = async (l) => {
    if (!window.confirm(`Delete line ${l.line_no}?`)) return;
    try { await api.delete(`/orders/${order.id}/lines/${l.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="line-items-tab">
      {editable && <div className="mb-3"><Button size="sm" onClick={() => setDlg({ open: true, line: null })} data-testid="add-line-btn"><Plus className="h-4 w-4 mr-1" />Add line</Button></div>}
      {!editable && order.status !== "draft" && <p className="text-xs text-slate-500 mb-3">{order.historical ? "Line items are locked after the first dispatch." : "Line items are locked after submission."}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="th text-left"><th className="px-3 py-2">#</th><th className="px-3 py-2">Item code</th><th className="px-3 py-2">Item name / description</th><th className="px-3 py-2">HSN</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Dispatched</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">GST</th><th className="px-3 py-2 text-right">Amount</th><th></th></tr></thead>
          <tbody>
            {order.line_items.map((l) => (
              <tr key={l.id} className="border-b border-slate-100" data-testid={`line-row-${l.line_no}`}>
                <td className="px-3 py-2 font-mono text-xs">{l.line_no}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.item_code || <span className="text-slate-400">free text</span>}{l.legacy_item_code && l.legacy_item_code !== l.item_code && <div className="text-[10px] text-slate-400">was {l.legacy_item_code}</div>}</td>
                <td className="px-3 py-2">{l.item_snapshot?.item_name || l.description}{l.item_snapshot && l.description !== l.item_snapshot.item_name && <div className="text-xs text-slate-500">{l.description}</div>}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.item_snapshot ? (l.item_snapshot.hsn_sac || <span className="text-amber-600" title="No HSN - needed before invoicing">missing</span>) : "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{l.qty} {l.uom}</td>
                <td className="px-3 py-2 text-right font-mono">{l.dispatched_qty}</td>
                <td className="px-3 py-2 text-right font-mono">{inr(l.unit_price)}</td>
                <td className="px-3 py-2 text-right font-mono">{l.tax_pct}%</td>
                <td className="px-3 py-2 text-right font-mono">{inr(l.amount)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {editable && <>
                    <Button variant="ghost" size="icon" onClick={() => setDlg({ open: true, line: l })} data-testid={`edit-line-${l.line_no}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(l)} data-testid={`delete-line-${l.line_no}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </>}
                </td>
              </tr>
            ))}
            {order.line_items.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-500">No line items yet</td></tr>}
          </tbody>
          <tfoot>
            <tr><td colSpan={8} className="px-3 py-2 text-right text-xs text-slate-500">Subtotal</td><td className="px-3 py-2 text-right font-mono">{inr(order.subtotal)}</td><td /></tr>
            <tr><td colSpan={8} className="px-3 py-1 text-right text-xs text-slate-500">GST</td><td className="px-3 py-1 text-right font-mono">{inr(order.tax_total)}</td><td /></tr>
            <tr><td colSpan={8} className="px-3 py-2 text-right text-xs font-semibold">Total</td><td className="px-3 py-2 text-right font-mono font-semibold" data-testid="order-grand-total">{inr(order.grand_total)}</td><td /></tr>
          </tfoot>
        </table>
      </div>
      <LineDialog open={dlg.open} onOpenChange={(v) => setDlg({ ...dlg, open: v })} order={order} line={dlg.line} onSaved={reload} />
    </div>
  );
}
